import { List, Map } from 'immutable';

import { getTemplateOperationsForProducts, setIsComplete, shapeTemplateForApp } from '../utils/profiles/templates';
import { updateItemSpecificsForCategory, updateProductDetailsForCategory } from '../utils/product/taxonomy';
import { shapeProfilesForApp, shapeProfileForAPI } from '../utils/profiles/profiles';
import { getProfileSections, setAll } from '../utils/profiles/sections';
import { shapeAiOperationsForApp } from '../utils/profiles/ai';
import { shapeOperationsForAPI } from '../utils/product/shapeForAPI';
import { shapeListingsForApp } from '../utils/profiles/listings';
import { setProductErrors } from '../utils/product/errors';
import { createController } from '../utils/controller';
import { getCopyTitle } from '../utils/profiles/getCopyTitle';
import { escapeRegex } from '../utils/string';
import { isTrue, not } from '../utils/bool';
import { newProduct } from '../utils/product/new';
import { decrease } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import { get } from '../utils/iterable/get';
import api from '../utils/api';

import { ALL, ERRORS, PAGE_SIZE, PROFILE_TYPE, SAVE_TYPE } from '../constants/profiles';
import { POLLING_INTERVAL, SYNC_INDICATOR } from '../constants/shops';
import { AMOUNT, ERROR, NEW, VALUE } from '../constants/product';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { CHANNEL_ID, EBAY } from '../constants/channels';
import { EMPTY, VELA } from '../constants';
import { ATTRIBUTE } from '../constants/attributes';
import { FEATURE } from '../constants/features';
import { STATUS } from '../constants/listings';
import { MODALS } from '../constants/modal';
import { FORM } from '../constants/forms';
import { DATA } from '../data';

import actions from '../actions';

const filtersController = createController();
const listingsController = createController();
const profilesController = createController();
const templatesController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    filtersController,
    listingsController,
    profilesController,
    templatesController,
  ].forEach(abort);
}

function* bootstrap(state, { shopId, type }) {
  abortAll();

  if (!shopId) {
    return state.set('profiles', EMPTY.MAP);
  }

  const shop = state.getIn(['shops', 'byId', shopId]);
  const channel = shop.get('channel');
  const db = shop.get('db');
  const profiles = Map({
    channel,
    db,
    filters: Map({ section: ALL, type }),
    pagination: Map({ page: 0, pageSize: PAGE_SIZE }),
    query: '',
    shopId,
  });

  yield function effect(dispatch) {
    dispatch(actions.data.getAllData(shopId));
    dispatch(actions.profiles.getFilters(true));
    dispatch(actions.profiles.getProfiles());
  };

  return state.set('profiles', profiles);
}

function* cleanUp(state) {
  abortAll();
  return state.set('profiles', EMPTY.MAP);
}

function* connectListings(state, { listings, profile: source, profileId }) {
  const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
  const profiles = state.get('profiles');
  const channel = profiles.get('channel');
  const db = profiles.get('db');
  const shopId = profiles.get('shopId');
  const profile = source || profiles.getIn(['byId', profileId]);
  const type = profile.get('type');

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        function onConnectFullfilled() {
          function onPropagateFullfilled() {
            dispatch(
              actions.shops.setData({
                path: ['syncData', shopId],
                value: Map({ indicator: SYNC_INDICATOR.SYNC }),
              })
            );

            dispatch(
              actions.shops.rescheduleShopsPolling(
                state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
                POLLING_INTERVAL.SHORT
              )
            );

            dispatch(actions.profiles.connectListings.fulfilled({ listings, profileId, shopId, type }));
          }

          function onPropagateRejected(error) {
            console.error(error);
            dispatch(
              actions.notifications.add({
                message: MESSAGE.FAIL.SYNC,
                type: NOTIFICATION.ERROR,
              })
            );
          }

          api.profiles
            .propagateProfile({ profileId, type })
            .then(onPropagateFullfilled)
            .catch(onPropagateRejected);
        }

        function onConnectRejected(error) {
          console.error(error);
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.SYNC,
              type: NOTIFICATION.ERROR,
            })
          );
        }

        api.profiles
          .connectListings({ listings, profileId, type })
          .then(onConnectFullfilled)
          .catch(onConnectRejected);
      };

      if (state.getIn(['connectProfile', 'loading'])) return state;

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
      const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
      const taxonomyData = get([channel, marketplaceId])(DATA);
      const titleFormatPreference = state.getIn(['shops', 'byId', shopId, 'titleFormatPreference']);

      if (!profile.has('template')) {
        yield function effect(dispatch) {
          const templateId = profile.get('templateId');

          function onTemplateFullfilled(data) {
            const template = shapeTemplateForApp({
              channel,
              data,
              sections: profile.get('sections'),
              shopData,
              shopId,
              taxonomyData,
              templateId,
              titleFormatPreference,
              userFeatures,
            });

            dispatch(
              actions.profiles.connectListings({
                listings,
                profile: profile.set('template', template),
                profileId,
              })
            );
          }

          function onTemplateRejected(error) {
            dispatch(actions.profiles.connectListings.rejected({ error }));
          }

          api.products
            .getById({ channel, db, payload: { id: [templateId] }, shopId })
            .then(onTemplateFullfilled)
            .catch(onTemplateRejected);
        };
      } else {
        yield function effect(dispatch) {
          function onConnectFullfilled() {
            const operations = getTemplateOperationsForProducts({ channel, profile, userFeatures });

            if (getSize(operations) === 0) {
              dispatch(actions.profiles.connectListings.fulfilled({ listings, profileId, shopId, type }));
              return;
            }

            const payload = {
              channel_id: CHANNEL_ID[channel],
              operations: shapeOperationsForAPI({ channel, operations }),
              userDb: db,
              shopId,
            };

            function onPropagateFullfilled() {
              dispatch(
                actions.shops.setData({
                  path: ['syncData', shopId],
                  value: Map({ indicator: SYNC_INDICATOR.SYNC }),
                })
              );

              dispatch(
                actions.shops.rescheduleShopsPolling(
                  state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
                  POLLING_INTERVAL.SHORT
                )
              );

              dispatch(actions.profiles.connectListings.fulfilled({ listings, profileId, shopId, type }));
            }

            function onPropagateRejected(error) {
              console.error(error);
              dispatch(
                actions.notifications.add({
                  message: MESSAGE.FAIL.SYNC,
                  type: NOTIFICATION.ERROR,
                })
              );
            }

            api.profiles
              .propagateProfile({ payload, profileId, type })
              .then(onPropagateFullfilled)
              .catch(onPropagateRejected);
          }

          function onConnectRejected(error) {
            dispatch(actions.profiles.connectListings.rejected({ error }));
          }

          api.profiles
            .connectListings({ listings, profileId, type })
            .then(onConnectFullfilled)
            .catch(onConnectRejected);
        };

        if (state.getIn(['connectProfile', 'loading'])) return state;
      }

      break;
    }

    default: {
      break;
    }
  }

  return state.setIn(['connectProfile', 'loading'], true);
}

function* connectListingsRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CONNECT_LISTINGS,
      })
    );
  };

  return state.has('connectProfile')
    ? state.deleteIn(['connectProfile', 'loading'])
    : state;
}

function* connectListingsFulfilled(source, { listings, profileId, shopId, type }) {
  yield function effect(dispatch) {
    dispatch(actions.data.getProfiles({ force: true, shopId, type }));
  };

  const state = source.has('connectProfile')
    ? source.delete('connectProfile')
    : source;

  if (
    state.getIn(['profiles', 'shopId']) !== shopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'byId', profileId])
  ) {
    return state;
  }

  function updateProfile(oldProfile) {
    function updateTotal(total) {
      return total + getSize(listings);
    }

    return oldProfile
      .setIn(['listings', 'hasMore'], true)
      .setIn(['listings', 'loading'], true)
      .deleteIn(['listings', 'page'])
      .updateIn(['listings', 'total'], updateTotal)
      .set('reloadAfterSave', true);
  }

  return state.updateIn(['profiles', 'byId', profileId], updateProfile);
}

function* copyProfile(state, { confirmed, profile: source, profileId, shopId: targetShopId }) {
  const profiles = state.get('profiles');
  const sourceShopId = profiles.get('shopId');
  const userShops = state.get('shops');
  let profile = source;

  if (!confirmed && sourceShopId === targetShopId && getSize(state.getIn(['profiles', 'editing'])) > 0) {
    yield function effect(dispatch) {
      dispatch(
        actions.profiles.setModal({
          action: actions.profiles.copyProfile({ confirmed: true, profile: source, profileId, shopId: targetShopId }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  if (!profile) {
    profile = profiles.getIn(['byId', profileId]).update('name', getCopyTitle);
  }

  const type = profile.get('type');
  const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        const channel = userShops.getIn(['byId', targetShopId, 'channel']);
        const db = userShops.getIn(['byId', targetShopId, 'db']);
        const payload = shapeProfileForAPI({ channel, profile, userFeatures });

        api.profiles
          .createProfile({ db, payload, shopId: targetShopId, type })
          .then(function onSucceeded(response) {
            dispatch(
              actions.profiles.copyProfile.fulfilled({
                profileId,
                response,
                sections: payload.sections,
                sourceShopId,
                targetShopId,
                type,
              })
            );
          })
          .catch(function onFailed(error) {
            dispatch(actions.profiles.copyProfile.rejected({ error, profileId, sourceShopId, type }));
          });
      };

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      if (!profile.has('template')) {
        yield function effect(dispatch) {
          const templateId = profile.get('templateId');
          const channel = profiles.get('channel');
          const db = profiles.get('db');
          const shopId = profiles.get('shopId');

          api.products
            .getById({ channel, db, payload: { id: [templateId] }, shopId })
            .then(function onSucceeded(data) {
              const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
              const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
              const taxonomyData = get([channel, marketplaceId])(DATA);
              const titleFormatPreference = state.getIn(['shops', 'byId', shopId, 'titleFormatPreference']);
              const template = shapeTemplateForApp({
                channel,
                data,
                sections: profile.get('sections'),
                shopData,
                shopId,
                taxonomyData,
                templateId,
                titleFormatPreference,
                userFeatures,
              });

              dispatch(
                actions.profiles.copyProfile({
                  profile: profile.set('template', template),
                  profileId,
                  targetShopId,
                })
              );
            })
            .catch(function onFailed(error) {
              dispatch(actions.profiles.copyProfile.rejected({ error, profileId, shopId, type }));
            });
        };
      } else {
        yield function effect(dispatch) {
          const db = userShops.getIn(['byId', targetShopId, 'db']);
          const channel = userShops.getIn(['byId', targetShopId, 'channel']);
          const shopData = state.getIn(['data', 'shopsData', targetShopId], EMPTY.MAP);
          const marketplaceId = state.getIn(['shops', 'byId', targetShopId, 'marketplaceId']);
          const taxonomyData = get([channel, marketplaceId])(DATA);
          const payload = shapeProfileForAPI({
            channel,
            db,
            profile,
            taxonomyData,
            shopData,
            shopId: targetShopId,
            userFeatures,
          });

          api.profiles
            .createProfile({ db, payload, shopId: targetShopId, type })
            .then(function onSucceeded(response) {
              dispatch(
                actions.profiles.copyProfile.fulfilled({
                  profileId,
                  response,
                  sections: payload.sections,
                  sourceShopId,
                  targetShopId,
                  type,
                })
              );
            })
            .catch(function onFailed(error) {
              dispatch(actions.profiles.copyProfile.rejected({ error, profileId, sourceShopId, type }));
            });
        };

        if (profiles.hasIn(['loading', profileId])) return state;
      }

      break;
    }

    default: {
      break;
    }
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  return state
    .setIn(['profiles', 'editing'], EMPTY.MAP)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* copyProfileRejected(state, { error, profileId, sourceShopId, type }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.COPY_PROFILE,
      })
    );
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) !== sourceShopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state;
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* copyProfileFulfilled(state, { profileId, response, sections, sourceShopId, targetShopId, type }) {
  let id;
  let profiles = state.get('profiles');
  const actionsToDispatch = [actions.data.getProfiles({ force: true, shopId: targetShopId, type })];

  switch (type) {
    case PROFILE_TYPE.AI: {
      id = response.aiProfileId.id;
      break;
    }

    case PROFILE_TYPE.STANDARD: {
      id = response.profileId.id;
      break;
    }

    default: {
      break;
    }
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (profiles.get('shopId') === targetShopId && profiles.getIn(['filters', 'type']) === type) {
    profiles = profiles.set('editing', Map({ id }));

    actionsToDispatch.push(
      actions.profiles.getFilters(),
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.COPY_PROFILE,
      })
    );

    if (profiles.getIn(['filters', 'section']) === ALL || sections[profiles.getIn(['filters', 'section'])]) {
      if (profiles.getIn(['pagination', 'page']) === 0) {
        profiles = profiles.set('scroll', true);

        actionsToDispatch.push(actions.profiles.getProfiles());
      } else {
        actionsToDispatch.push(actions.profiles.setPage({ page: 0 }));
      }
    } else {
      actionsToDispatch.push(actions.profiles.setSection({ section: ALL }));
    }
  } else {
    actionsToDispatch.push(
      actions.notifications.add({
        profileId: id,
        profileType: type,
        shopId: targetShopId,
        shopName: state.getIn(['shops', 'byId', targetShopId, 'name']),
        type: NOTIFICATION.PROFILE_COPIED,
      })
    );

    if (
      profiles.get('shopId') === sourceShopId &&
      profiles.getIn(['filters', 'type']) === type &&
      profiles.hasIn(['loading', profileId])
    ) {
      profiles = profiles.update('loading', updateLoading);
    }
  }

  yield function effect(dispatch) {
    actionsToDispatch.forEach(dispatch);
  };

  return state.set('profiles', profiles);
}

function* deleteProfile(state, { confirmed, profileId }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW && (
      state.getIn(['profiles', 'editing', 'id']) === profileId ||
      getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
    )
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profiles.setModal({
          action: actions.profiles.deleteProfile({ confirmed: true, profileId }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  yield function effect(dispatch) {
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);
    const shopId = state.getIn(['profiles', 'shopId']);

    api.profiles
      .deleteProfile({ profileId, type })
      .then(function onSucceeded() {
        dispatch(actions.profiles.deleteProfile.fulfilled({ profileId, shopId, type }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.deleteProfile.rejected({ error, profileId, shopId, type }));
      });
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  function updateEditing(editing = EMPTY.MAP) {
    return (
      editing.has('id') &&
      editing.get('id') !== profileId && (
        editing.get('id') === NEW ||
        getSize(editing.delete('id')) === 0
      )
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .updateIn(['profiles', 'loading'], updateLoading)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* deleteProfileRejected(state, { error, profileId, shopId, type }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_PROFILE,
      })
    );
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) === shopId &&
    state.getIn(['profiles', 'filters', 'type']) === type &&
    state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state.updateIn(['profiles', 'loading'], updateLoading);
  }

  return state;
}

function* deleteProfileFulfilled(state, { profileId, shopId, type }) {
  yield function effect(dispatch) {
    const actionsToDispatch = [
      actions.data.getProfiles({ force: true, shopId, type }),
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_PROFILE,
      }),
    ];

    if (state.getIn(['profiles', 'shopId']) === shopId && state.getIn(['profiles', 'filters', 'type']) === type) {
      actionsToDispatch.push(actions.profiles.getFilters());

      if (state.hasIn(['profiles', 'loading', profileId])) {
        actionsToDispatch.push(actions.profiles.getProfiles());
      }
    }

    actionsToDispatch.forEach(dispatch);
  };

  return state;
}

function* disconnectListing(state, { index, productId, profileId }) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const shopId = state.getIn(['profiles', 'shopId']);
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);

    api.profiles
      .disconnectListings({ listings: [productId], profileId, signal, type })
      .then(function onSucceeded() {
        dispatch(actions.profiles.disconnectListing.fulfilled({ index, productId, profileId, shopId, type }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.disconnectListing.rejected({ error, productId, profileId, signal, shopId, type }));
      });
  };

  return state.setIn(['profiles', 'byId', profileId, 'listings', 'byId', productId, 'processing'], true);
}

function* disconnectListingRejected(state, { error, productId, profileId, signal, shopId, type }) {
  if (
    signal.aborted ||
    shopId !== state.getIn(['profiles', 'shopId']) ||
    type !== state.getIn(['profiles', 'filters', 'type'])
  ) {
    return state;
  }

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DISCONNECT_LISTINGS,
      })
    );
  };

  if (state.hasIn(['profiles', 'byId', profileId, 'listings', 'byId', productId])) {
    return state.deleteIn(['profiles', 'byId', profileId, 'listings', 'byId', productId, 'processing']);
  }

  return state;
}

function* disconnectListingFulfilled(state, { index, productId, profileId, shopId, type }) {
  let data = state.get('data');
  let profiles = state.get('profiles');

  function updateIds(ids) {
    return ids.splice(index, 1);
  }

  function updateListings(source) {
    let listings = source
      .deleteIn(['byId', productId])
      .delete('loading')
      .update('total', decrease);

    if (listings.has('ids')) {
      listings = listings.update('ids', updateIds);
    }

    if (!listings.get('total')) {
      listings = listings.delete('animating').delete('opened');
    }

    return listings;
  }

  if (data.hasIn(['shopsData', shopId, 'profiles', type, 'byId', profileId])) {
    data = data.updateIn(['shopsData', shopId, 'profiles', type, 'byId', profileId, 'listings'], decrease);
  }

  if (
    shopId === profiles.get('shopId') &&
    type === profiles.getIn(['filters', 'type']) &&
    profiles.hasIn(['byId', profileId, 'listings'])
  ) {
    abort(listingsController);
    profiles = profiles.updateIn(['byId', profileId, 'listings'], updateListings);
  }

  return state.set('data', data).set('profiles', profiles);
}

function* edit(state, { confirmed, profileId }) {
  const showConfirmModal = function effect(dispatch) {
    dispatch(
      actions.profiles.setModal({
        action: actions.profiles.edit({ confirmed: true, profileId }),
        type: MODALS.PROFILES.CANCEL,
      })
    );
  };

  if (profileId === state.getIn(['profiles', 'editing', 'id'])) {
    if (!confirmed) {
      const profile = state.getIn(['profiles', 'editing']);

      if (profileId === NEW) {
        if (getSize(profile.get('name')) > 0 || profile.get('sections').some(isTrue)) {
          yield showConfirmModal;
          return state;
        }
      } else {
        const type = profile.get('type');
        const original = state.getIn(['profiles', 'byId', profileId], EMPTY.MAP);

        if (
          profile.get('name') !== original.get('name') ||
          !profile.get('sections').equals(original.get('sections'))
        ) {
          yield showConfirmModal;
          return state;
        }

        switch (type) {
          case PROFILE_TYPE.AI: {
            if (!profile.get('ai').equals(original.get('ai'))) {
              yield showConfirmModal;
              return state;
            }

            break;
          }

          case PROFILE_TYPE.STANDARD: {
            if (
              getSize(profile.getIn(['template', 'operations'])) > 0 || (
                state.getIn(['user', 'features', FEATURE.TEMPLATE_MODE_PROFILES]) &&
                profile.get('templateMode') !== original.get('templateMode')
              )
            ) {
              yield showConfirmModal;
              return state;
            }

            break;
          }

          default: {
            break;
          }
        }
      }
    }

    return state.setIn(['profiles', 'editing'], EMPTY.MAP);
  }

  if (!confirmed && getSize(state.getIn(['profiles', 'editing'])) > 0) {
    yield showConfirmModal;
    return state;
  }

  if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  if (profileId === NEW) {
    const channel = state.getIn(['profiles', 'channel']);
    const type = state.getIn(['profiles', 'filters', 'type']);
    let profile = Map({
      name: '',
      id: NEW,
      sections: setAll(getProfileSections({ channel, type })),
      type,
    });

    function reduceAISections(result, value, section) {
      switch (section) {
        case FORM.DESCRIPTION: {
          return result.set(ATTRIBUTE.DESCRIPTION, EMPTY.MAP);
        }

        case FORM.SEO: {
          return result
            .set(ATTRIBUTE.META_DESCRIPTION, EMPTY.MAP)
            .set(ATTRIBUTE.PAGE_TITLE, EMPTY.MAP);
        }

        case FORM.TAGS: {
          return result.set(ATTRIBUTE.TAGS, EMPTY.MAP);
        }

        case FORM.TITLE: {
          return result.set(ATTRIBUTE.TITLE, EMPTY.MAP);
        }

        default: {
          return result;
        }
      }
    }

    switch (type) {
      case PROFILE_TYPE.AI: {
        profile = profile.set('ai', profile.get('sections').reduce(reduceAISections, EMPTY.MAP));
        break;
      }

      case PROFILE_TYPE.STANDARD: {
        const shopId = state.getIn(['profiles', 'shopId']);
        const template = newProduct({ asProfile: true, channel })
          .set(ERROR, EMPTY.MAP)
          .set('shopId', shopId)
          .set('status', STATUS[VELA].TEMPLATE);

        profile = profile
          .set('template', template)
          .set('templateMode', false);

        break;
      }

      default: {
        break;
      }
    }

    return state.setIn(['profiles', 'editing'], profile);
  } else if (state.hasIn(['profiles', 'byId', profileId])) {
    const profile = state.getIn(['profiles', 'byId', profileId]);

    switch (profile.get('type')) {
      case PROFILE_TYPE.AI: {
        if (!profile.has('ai')) {
          yield function effect(dispatch) {
            dispatch(actions.profiles.getAiOperations({ profileIds: List([profileId]) }));
          };
        }

        break;
      }

      case PROFILE_TYPE.STANDARD: {
        if (!profile.has('template')) {
          const templateId = profile.get('templateId');

          yield function effect(dispatch) {
            dispatch(
              actions.profiles.getTemplates(
                Map({
                  profileIds: List([profileId]),
                  templateIds: List([templateId]),
                  templateToProfileMap: Map({ [templateId]: profileId }),
                })
              )
            );
          };
        }

        break;
      }

      default: {
        break;
      }
    }

    return state.setIn(['profiles', 'editing'], profile);
  }

  return state.setIn(['profiles', 'editing'], Map({ id: profileId }));
}

function* getAiOperations(state, { profileIds }) {
  if (!state.hasIn(['profiles', 'shopId']) || getSize(profileIds) === 0) return state;

  abort(templatesController);
  const profiles = state.get('profiles');

  yield function effect(dispatch) {
    const { signal } = templatesController.start();

    function mapProfileIds(profileId) {
      return api.profiles.getById({ profileId, signal, type: PROFILE_TYPE.AI });
    }

    const requests = profileIds.map(mapProfileIds);

    Promise.all(requests)
      .then(function onSucceeded(responses) {
        dispatch(actions.profiles.getAiOperations.fulfilled({ profileIds, responses }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.getAiOperations.rejected({ error, profileIds, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('loading', EMPTY.SET).union(profileIds.toSet()));
}

function* getAiOperationsFulfilled(state, { profileIds, responses }) {
  abort(templatesController);

  function reduceResponses(result, response, index) {
    const profileId = profileIds.get(index);

    if (!result.hasIn(['byId', profileId])) return result;

    let profiles = result;
    const ai = shapeAiOperationsForApp(response.data);

    profiles = profiles
      .setIn(['byId', profileId, 'ai'], ai)
      .update('loading', function updateLoading(loading = EMPTY.SET) {
        return loading.delete(profileId);
      });

    if (profiles.getIn(['editing', 'id']) === profileId) {
      profiles = profiles.setIn(['editing', 'ai'], ai);
    }

    return profiles;
  }

  return state.set('profiles', responses.reduce(reduceResponses, state.get('profiles')));
}

function* getAiOperationsRejected(state, { error, profileIds, signal }) {
  if (signal.aborted) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  function updateEditing(editing = EMPTY.MAP) {
    const id = editing.get('id');

    if (id && profileIds.includes(id)) {
      return EMPTY.MAP;
    }

    return editing;
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.subtract(profileIds.toSet());
  }

  return state
    .updateIn(['profiles', 'editing'], updateEditing)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* getFilters(state, initial) {
  if (!state.hasIn(['profiles', 'shopId'])) return state;

  abort(filtersController);

  const profiles = state.get('profiles');
  const shopId = profiles.get('shopId');

  if (!shopId) {
    return state;
  }

  if (initial) {
    const db = profiles.get('db');
    const { signal } = filtersController.start();
    const requests = [api.profiles.getFilters({ db, shopId, signal, type: PROFILE_TYPE.STANDARD })];

    if (state.getIn(['user', 'features', FEATURE.AI_PROFILES])) {
      requests.push(api.profiles.getFilters({ db, shopId, signal, type: PROFILE_TYPE.AI }));
    } else {
      requests.push(Promise.resolve({ [ALL]: 0 }));
    }

    yield function effect(dispatch) {
      Promise
        .all(requests)
        .then(function onSucceeded(responses) {
          abort(filtersController);
          const [standard, ai] = responses;

          dispatch(
            actions.profiles.getFilters.fulfilled({
              initial,
              response: standard,
              type: PROFILE_TYPE.STANDARD,
            })
          );

          dispatch(actions.profiles.getFilters.fulfilled({ response: ai, type: PROFILE_TYPE.AI }));
        })
        .catch(function onFailed(error) {
          if (!signal.aborted) {
            abort(filtersController);
            dispatch(actions.profiles.getFilters.rejected({ error }));
          }
        });
    };
  } else {
    const type = profiles.getIn(['filters', 'type']);

    if (
      type === PROFILE_TYPE.AI &&
      !state.getIn(['user', 'features', FEATURE.AI_PROFILES])
    ) {
      return state
        .setIn(['profiles', 'filters', 'section'], ALL)
        .setIn(['profiles', 'filters', 'sections'], Map({ [ALL]: 0 }))
        .setIn(['profiles', 'filters', 'types', PROFILE_TYPE.AI], 0);
    }

    yield function effect(dispatch) {
      const db = profiles.get('db');
      const { signal } = filtersController.start();

      api.profiles
        .getFilters({ db, shopId, signal, type })
        .then(function onSucceeded(response) {
          abort(filtersController);
          dispatch(actions.profiles.getFilters.fulfilled({ response, type }));
        })
        .catch(function onFailed(error) {
          if (!signal.aborted) {
            abort(filtersController);
            dispatch(actions.profiles.getFilters.rejected({ error }));
          }
        });
    };
  }

  return state.setIn(['profiles', 'filters', 'loading'], true);
}

function* getFiltersRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return state.deleteIn(['profiles', 'filters', 'loading']);
}

function* getFiltersFulfilled(state, { response, type }) {
  const count = response[ALL] || 0;
  let profiles = state
    .get('profiles')
    .deleteIn(['filters', 'loading'])
    .setIn(['filters', 'types', type], count);

  if (profiles.getIn(['filters', 'type']) === type) {
    profiles = profiles.setIn(['filters', 'sections'], Map(response));
  }

  return state.set('profiles', profiles);
}

function* getListings(state, profileId) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  const listings = state.getIn(['profiles', 'byId', profileId, 'listings']);
  const page = listings.get('page', -1) + 1;
  const limit = PAGE_SIZE;
  const offset = page * limit;

  if (offset >= listings.get('total')) return state;

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);

    api.profiles
      .getListings({ limit, offset, profileId, signal, type })
      .then(function onSucceeded(response) {
        dispatch(actions.profiles.getListings.fulfilled({ page, profileId, response }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.getListings.rejected({ error, profileId, signal }));
      });
  };

  return state.setIn(['profiles', 'byId', profileId, 'listings', 'loading'], true);
}

function* getListingsRejected(state, { error, profileId, signal }) {
  if (signal.aborted || !state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['profiles', 'byId', profileId, 'listings', 'loading']);
}

function* getListingsFulfilled(state, { page, profileId, response }) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  function updateListings(listings) {
    return shapeListingsForApp({ response, state: listings.delete('loading').set('page', page) });
  }

  return state.updateIn(['profiles', 'byId', profileId, 'listings'], updateListings);
}

function* getProfiles(state) {
  if (!state.hasIn(['profiles', 'shopId'])) return state;

  abort(profilesController);
  abort(templatesController);

  const profiles = state.get('profiles');
  const shopId = profiles.get('shopId');
  const type = profiles.getIn(['filters', 'type']);

  if (!shopId) {
    return state;
  }

  if (
    type === PROFILE_TYPE.AI &&
    !state.getIn(['user', 'features', FEATURE.AI_PROFILES])
  ) {
    return state
      .setIn(['profiles', 'byId'], EMPTY.MAP)
      .setIn(['profiles', 'ids'], EMPTY.LIST)
      .setIn(['profiles', 'loading'], EMPTY.SET)
      .setIn(['profiles', 'request'], EMPTY.MAP)
      .setIn(['profiles', 'total'], 0)
      .setIn(['profiles', 'section'], ALL)
      .setIn(['profiles', 'editing'], EMPTY.MAP)
      .setIn(['profiles', 'pagination'], Map({ page: 0, pageSize: PAGE_SIZE }));
  }

  yield function effect(dispatch) {
    const db = profiles.get('db');
    const limit = profiles.getIn(['pagination', 'pageSize']);
    const offset = profiles.getIn(['pagination', 'page']) * limit;
    const section = profiles.getIn(['filters', 'section']);
    const name = profiles.get('query');
    const request = { db, limit, name, offset, section, shopId, type };
    const { signal } = profilesController.start();

    api.profiles
      .getProfiles({ ...request, signal })
      .then(function onSucceeded(response) {
        dispatch(actions.profiles.getProfiles.fulfilled({ request, response }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.getProfiles.rejected({ error, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('ids', EMPTY.LIST).toSet());
}

function* getProfilesRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(profilesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return state.setIn(['profiles', 'loading'], EMPTY.SET);
}

function* getProfilesFulfilled(state, { request, response }) {
  abort(profilesController);

  if (!response.count) {
    if (
      request.section !== ALL &&
      state.getIn(['profiles', 'request', 'section'], state.getIn(['profiles', 'filters', 'section'])) !== ALL
    ) {
      yield function effect(dispatch) {
        dispatch(actions.profiles.setSection({ section: ALL }));
      };

      return state;
    }

    if (
      request.offset > 0 &&
      state.getIn(['profiles', 'pagination', 'page'], 0) > 0
    ) {
      yield function effect(dispatch) {
        dispatch(actions.profiles.setPage({ page: 0 }));
      };

      return state;
    }
  } else if (getSize(response.data) === 0) {
    if (state.getIn(['profiles', 'pagination', 'page'], 0) > 0) {
      yield function effect(dispatch) {
        dispatch(actions.profiles.setPage({ page: Math.max(0, Math.floor((response.count - 1) / PAGE_SIZE)) }));
      };

      return state;
    }
  }

  const {
    actions: actionsToDispatch,
    profiles,
  } = shapeProfilesForApp({ profiles: state.get('profiles'), request, response });

  if (getSize(actionsToDispatch) > 0) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return state.set('profiles', profiles);
}

function* getTemplates(state, templates) {
  if (!state.hasIn(['profiles', 'shopId']) || getSize(templates) === 0) return state;

  const profiles = state.get('profiles');
  const profileIds = templates.get('profileIds');
  const templateIds = templates.get('templateIds');
  const templateToProfileMap = templates.get('templateToProfileMap');

  if (getSize(templateIds) === 0) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    const { signal } = templatesController.start();
    const channel = profiles.get('channel');
    const db = profiles.get('db');
    const shopId = profiles.get('shopId');

    api.products
      .getById({ channel, db, payload: { id: templateIds.toArray() }, shopId, signal })
      .then(function onSucceeded(data) {
        dispatch(
          actions.profiles.getTemplates.fulfilled({
            channel,
            data,
            shopId,
            signal,
            templateIds,
            templateToProfileMap,
          })
        );
      })
      .catch(function onFailed(error) {
        dispatch(actions.profiles.getTemplates.rejected({ error, profileIds, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('loading', EMPTY.SET).union(profileIds.toSet()));
}

function* getTemplatesRejected(state, { error, profileIds, signal }) {
  if (signal.aborted) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  function updateEditing(editing = EMPTY.MAP) {
    const id = editing.get('id');

    if (id && profileIds.includes(id)) {
      return EMPTY.MAP;
    }

    return editing;
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.subtract(profileIds.toSet());
  }

  return state
    .updateIn(['profiles', 'editing'], updateEditing)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* getTemplatesFulfilled(state, { channel, data, shopId, signal, templateIds, templateToProfileMap }) {
  const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
  const taxonomyData = get([channel, marketplaceId])(DATA);
  let profiles = state.get('profiles');
  let failed = EMPTY.LIST;

  for (const templateId of templateIds) {
    const profileId = templateToProfileMap.get(templateId);

    if (!profiles.hasIn(['byId', profileId])) continue;

    try {
      const sections = profiles.getIn(['byId', profileId, 'sections']);
      const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
      const titleFormatPreference = state.getIn(['shops', 'byId', shopId, 'titleFormatPreference']);
      let template = shapeTemplateForApp({
        channel,
        data,
        sections,
        shopData,
        shopId,
        taxonomyData,
        templateId,
        titleFormatPreference,
        userFeatures,
      });

      if (channel === EBAY) {
        const category = template.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

        if (
          category &&
          category !== AMOUNT.ZERO &&
          !state.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])
        ) {
          template = template.set('loading', true);

          yield function effect(dispatch) {
            dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
          };
        }
      }

      profiles = profiles
        .setIn(['byId', profileId, 'template'], template)
        .update('loading', function updateLoading(loading = EMPTY.SET) {
          return loading.delete(profileId);
        });

      if (profiles.getIn(['editing', 'id']) === profileId) {
        profiles = profiles.setIn(['editing', 'template'], template);
      }
    } catch (error) {
      console.error(error);
      failed = failed.push(profileId);
    }
  }

  if (getSize(failed)) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.getTemplates.rejected({ profileIds: failed, signal }));
    };
  } else {
    abort(templatesController);
  }

  return state.set('profiles', profiles);
}

function* saveProfile(state, { type: saveType }) {
  const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
  const profiles = state.get('profiles');
  const db = profiles.get('db');
  const shopId = profiles.get('shopId');
  const channel = profiles.get('channel');
  const profile = profiles.get('editing');
  const profileId = profile.get('id');
  const type = profile.get('type');
  const original = state.getIn(['profiles', 'byId', profileId], EMPTY.MAP);

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  if (saveType === SAVE_TYPE.UPDATE_PROFILE && profile.get('sections').equals(original.get('sections'))) {
    switch (type) {
      case PROFILE_TYPE.AI: {
        if (profile.get('ai').equals(original.get('ai'))) {
          if (profile.get('name') === original.get('name')) {
            return state.deleteIn(['profiles', 'editing'], EMPTY.MAP);
          }

          yield function effect(dispatch) {
            const payload = { name: profile.get('name') };

            function onFailed(error) {
              dispatch(actions.profiles.saveProfile.rejected({ error, profile, profileId, saveType, shopId, type }));
            }

            function onSucceeded(response) {
              dispatch(
                actions.profiles.saveProfile.fulfilled({
                  channel,
                  db,
                  partial: true,
                  profile,
                  profileId,
                  response,
                  saveType,
                  shopId,
                  type,
                })
              );
            }

            api.profiles
              .partialUpdateProfile({ payload, profileId, type })
              .then(onSucceeded)
              .catch(onFailed);
          };

          return state.updateIn(['profiles', 'loading'], updateLoading);
        }

        break;
      }

      case PROFILE_TYPE.STANDARD: {
        if (getSize(profile.getIn(['template', 'operations'])) === 0) {
          const payload = {};

          if (profile.get('name') !== original.get('name')) {
            payload.name = profile.get('name');
          }

          if (
            state.getIn(['user', 'features', FEATURE.TEMPLATE_MODE_PROFILES]) &&
            profile.get('templateMode') !== original.get('templateMode')
          ) {
            payload.template_mode = profile.get('templateMode');
          }

          if (getSize(payload) === 0) {
            return state.setIn(['profiles', 'editing'], EMPTY.MAP);
          }

          yield function effect(dispatch) {
            function onFailed(error) {
              dispatch(actions.profiles.saveProfile.rejected({ error, profile, profileId, saveType, shopId, type }));
            }

            function onSucceeded(response) {
              dispatch(
                actions.profiles.saveProfile.fulfilled({
                  channel,
                  db,
                  partial: true,
                  profile,
                  profileId,
                  response,
                  saveType,
                  shopId,
                  type,
                })
              );
            }

            api.profiles
              .partialUpdateProfile({ payload, profileId, type })
              .then(onSucceeded)
              .catch(onFailed);
          };

          return state.updateIn(['profiles', 'loading'], updateLoading);
        }

        break;
      }

      default: {
        break;
      }
    }
  }

  yield function effect(dispatch) {
    const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
    const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
    const taxonomyData = get([channel, marketplaceId])(DATA);
    const payload = shapeProfileForAPI({ channel, db, profile, taxonomyData, shopData, shopId, userFeatures });

    function onFailed(error) {
      dispatch(actions.profiles.saveProfile.rejected({ error, profile, profileId, saveType, shopId, type }));
    }

    function onSucceeded(response) {
      dispatch(
        actions.profiles.saveProfile.fulfilled({
          channel,
          db,
          profile,
          profileId,
          response,
          saveType,
          sections: payload.sections,
          shopId,
          type,
        })
      );
    }

    switch (saveType) {
      case SAVE_TYPE.SAVE_AS_NEW: {
        api.profiles
          .createProfile({ db, payload, shopId, type })
          .then(onSucceeded)
          .catch(onFailed);

        break;
      }

      default: {
        api.profiles
          .updateProfile({ payload, profileId, type })
          .then(onSucceeded)
          .catch(onFailed);

        break;
      }
    }
  };

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* saveProfileRejected(state, { error, profile, profileId, saveType, shopId, type }) {
  function updateEditing(editing = EMPTY.MAP) {
    return editing.setIn([ERROR, 'name'], ERRORS.SAME_NAME_PROFILE);
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) === shopId &&
    state.getIn(['profiles', 'filters', 'type']) === type &&
    state.getIn(['profiles', 'editing', 'id']) === profileId
  ) {
    if (typeof error === 'string' && new RegExp(escapeRegex(profile.get('name'))).test(error)) {
      return state
        .updateIn(['profiles', 'editing'], updateEditing)
        .updateIn(['profiles', 'loading'], updateLoading);
    }
  }

  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: saveType === SAVE_TYPE.SAVE_AS_NEW
          ? MESSAGE.FAIL.CREATE_PROFILE
          : MESSAGE.FAIL.SAVE_PROFILE,
      })
    );
  };

  if (
    state.getIn(['profiles', 'shopId']) !== shopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state;
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* saveProfileFulfilled(state, action) {
  const { channel, db, partial, profile, profileId, response, saveType, sections, shopId, type } = action;
  let profiles = state.get('profiles');
  const actionsToDispatch = [
    actions.data.getProfiles({ force: true, shopId, type }),
    actions.notifications.add({
      type: NOTIFICATION.SUCCESS,
      message: saveType === SAVE_TYPE.SAVE_AS_NEW
        ? MESSAGE.SUCCESS.CREATE_PROFILE
        : MESSAGE.SUCCESS.SAVE_PROFILE,
    }),
  ];

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (profiles.get('shopId') === shopId && profiles.getIn(['filters', 'type']) === type) {
    if (profiles.getIn(['editing', 'id']) === profileId) {
      profiles = profiles.set('editing', EMPTY.MAP);
    }

    if (partial) {
      if (profiles.hasIn(['byId', profileId])) {
        profiles = profiles.setIn(['byId', profileId], profile);
      }

      if (profiles.hasIn(['loading', profileId])) {
        profiles = profiles.update('loading', updateLoading);
      }
    } else {
      if (saveType === SAVE_TYPE.UPDATE_PROFILE && profiles.hasIn(['byId', profileId])) {
        switch (type) {
          case PROFILE_TYPE.AI: {
            profiles = profiles.deleteIn(['byId', profileId, 'ai']);
            break;
          }

          case PROFILE_TYPE.STANDARD: {
            profiles = profiles.deleteIn(['byId', profileId, 'template']);
            break;
          }

          default: {
            break;
          }
        }
      }

      actionsToDispatch.push(actions.profiles.getFilters());

      if (profiles.getIn(['filters', 'section']) === ALL || sections[profiles.getIn(['filters', 'section'])]) {
        if (saveType === SAVE_TYPE.SAVE_AS_NEW) {
          if (profiles.getIn(['pagination', 'page']) === 0) {
            let id;
            let newProfile = profile.set('listings', Map({ total: 0 }));

            switch (type) {
              case PROFILE_TYPE.AI: {
                id = response.aiProfileId;
                newProfile = newProfile.set('id', id);
                break;
              }

              case PROFILE_TYPE.STANDARD: {
                id = response.profileId;
                newProfile = newProfile.delete('template').set('id', id);
                break;
              }

              default: {
                break;
              }
            }

            profiles = profiles
              .set('scroll', true)
              .set('ids', profiles.get('ids').unshift(id))
              .set('loading', profiles.get('loading', EMPTY.SET).add(id))
              .setIn(['byId', id], newProfile);

            actionsToDispatch.push(actions.profiles.getProfiles());
          } else {
            actionsToDispatch.push(actions.profiles.setPage({ page: 0 }));
          }
        } else {
          actionsToDispatch.push(actions.profiles.getProfiles());
        }
      } else {
        actionsToDispatch.push(actions.profiles.setSection({ section: ALL }));
      }
    }
  }

  if (
    !partial &&
    saveType === SAVE_TYPE.UPDATE_PROFILE &&
    profile.getIn(['listings', 'total']) > 0 && (
      type !== PROFILE_TYPE.STANDARD ||
      !profile.get('templateMode')
    )
  ) {
    yield function effect(dispatch) {
      const payload = {
        channel_id: CHANNEL_ID[channel],
        userDb: db,
        shopId,
      };

      if (type === PROFILE_TYPE.STANDARD) {
        const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
        const operations = getTemplateOperationsForProducts({ channel, profile, userFeatures });

        if (getSize(operations) === 0) {
          return;
        }

        payload.operations = shapeOperationsForAPI({ channel, operations });
      }

      api.profiles
        .propagateProfile({ payload, profileId, type })
        .then(function onSucceeded() {
          dispatch(
            actions.shops.setData({
              path: ['syncData', shopId],
              value: Map({ indicator: SYNC_INDICATOR.SYNC }),
            })
          );

          dispatch(
            actions.shops.rescheduleShopsPolling(
              state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
              POLLING_INTERVAL.SHORT
            )
          );
        })
        .catch(function onFailed(error) {
          console.error(error);
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.SYNC,
              type: NOTIFICATION.ERROR,
            })
          );
        });
    };
  }

  yield function effect(dispatch) {
    actionsToDispatch.forEach(dispatch);
  };

  return state.set('profiles', profiles);
}

function* search(state, { confirmed, query }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profiles.setModal({
          action: actions.profiles.search({ confirmed: true, query }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profiles.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'query'], query)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setData(source, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['profiles', ...path]
    : ['profiles', path];

  if (value !== undefined) return source.setIn(fullPath, value);

  let state = source.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* setModal(state, payload) {
  return payload
    ? state.setIn(['profiles', 'modal'], Map(payload))
    : state.deleteIn(['profiles', 'modal']);
}

function* setPage(state, { confirmed, page }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profiles.setModal({
          action: actions.profiles.setPage({ confirmed: true, page }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profiles.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'pagination', 'page'], page)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setSection(state, { confirmed, section }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profiles.setModal({
          action: actions.profiles.setSection({ confirmed: true, section }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profiles.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profiles.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'filters', 'section'], section)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setType(state, { type }) {
  yield function effect(dispatch) {
    dispatch(actions.profiles.getFilters());
    dispatch(actions.profiles.getProfiles());
  };

  return state
    .deleteIn(['profiles', 'modal'])
    .setIn(['profiles', 'editing'], EMPTY.MAP)
    .setIn(['profiles', 'filters', 'section'], ALL)
    .setIn(['profiles', 'filters', 'type'], type)
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'scroll'], true);
}

function* showConnectModal(state, { profileId }) {
  yield function effect(dispatch) {
    const profiles = state.get('profiles');
    const channel = profiles.get('channel');
    const db = profiles.get('db');
    const shopId = profiles.get('shopId');
    const profile = profiles.getIn(['byId', profileId]);

    if (profile.get('templateMode')) return;

    dispatch(actions.connectProfile.bootstrap({ channel, db, profileId, shopId }));

    if (profile.getIn(['listings', 'total']) > 0) {
      const listings = profile.getIn(['listings', 'ids']);

      if (getSize(listings) === profile.getIn(['listings', 'total'])) {
        dispatch(actions.connectProfile.setConnectedProducts(listings));
      } else {
        const { signal } = listingsController.start();

        api.profiles
          .getListings({ limit: 'null', offset: 'null', profileId, signal, type: profile.get('type') })
          .then(function onSucceeded(response) {
            dispatch(actions.profiles.showConnectModal.fulfilled({ profileId, response }));
          })
          .catch(function onFailed(error) {
            dispatch(actions.profiles.showConnectModal.rejected({ error, profileId, signal }));
          });
      }
    } else {
      dispatch(actions.connectProfile.setConnectedProducts(EMPTY.LIST));
    }
  };

  return state;
}

function* showConnectModalRejected(state, { error, profileId, signal }) {
  if (signal.aborted || !state.has('connectProfile')) return state;

  yield function effect(dispatch) {
    dispatch(actions.profiles.getListings.rejected({ error, profileId, signal }));
    dispatch(actions.connectProfile.setConnectedProducts(EMPTY.LIST));
  };

  return state;
}

function* showConnectModalFulfilled(state, { profileId, response }) {
  abort(listingsController);

  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  const listings = shapeListingsForApp({
    response,
    state: state
      .getIn(['profiles', 'byId', profileId, 'listings'])
      .delete('loading')
      .set('page', 0),
  });

  if (state.has('connectProfile')) {
    yield function effect(dispatch) {
      dispatch(actions.connectProfile.setConnectedProducts(listings.get('ids')));
    };
  }

  return state.setIn(['profiles', 'byId', profileId, 'listings'], listings);
}

function* toggleTemplateMode(state, { profileId }) {
  const profiles = state.get('profiles');

  if (
    profiles.getIn(['editing', 'type']) === PROFILE_TYPE.STANDARD &&
    profiles.getIn(['editing', 'id']) === profileId
  ) {
    return state.updateIn(['profiles', 'editing', 'templateMode'], not);
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  if (profiles.getIn(['byId', profileId, 'type']) === PROFILE_TYPE.STANDARD) {
    yield function effect(dispatch) {
      const shopId = profiles.get('shopId');
      const profile = profiles.getIn(['byId', profileId]).update('templateMode', not);
      const type = profile.get('type');
      const payload = {
        template_mode: profile.get('templateMode'),
      };

      function onFailed(error) {
        dispatch(actions.profiles.toggleTemplateMode.rejected({ error, profileId, shopId, type }));
      }

      function onSucceeded() {
        dispatch(actions.profiles.toggleTemplateMode.fulfilled({ profile, profileId, shopId, type }));
      }

      api.profiles
        .partialUpdateProfile({ payload, profileId, type })
        .then(onSucceeded)
        .catch(onFailed);
    };

    return state.updateIn(['profiles', 'loading'], updateLoading);
  }

  return state;
}

function* toggleTemplateModeFulfilled(state, { profile, profileId, shopId, type }) {
  let profiles = state.get('profiles');

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (profiles.get('shopId') === shopId && profiles.getIn(['filters', 'type']) === type) {
    if (profiles.hasIn(['byId', profileId])) {
      profiles = profiles.setIn(['byId', profileId], profile);
    }

    if (profiles.hasIn(['loading', profileId])) {
      profiles = profiles.update('loading', updateLoading);
    }
  }

  yield function effect(dispatch) {
    dispatch(actions.data.getProfiles({ force: true, shopId, type }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.SAVE_PROFILE,
      })
    );
  };

  return state.set('profiles', profiles);
}

function* toggleTemplateModeRejected(state, { error, profileId, shopId, type }) {
  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.SAVE_PROFILE,
      })
    );
  };

  if (
    state.getIn(['profiles', 'shopId']) !== shopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state;
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* updateTemplateAttributes(state, { profileId }) {
  yield function effect(dispatch) {
    const profiles = state.get('profiles', EMPTY.MAP);

    if (!profiles.hasIn(['byId', profileId])) return;

    const shopId = profiles.get('shopId');
    const channel = profiles.get('channel');
    const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
    const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
    const taxonomyData = get([channel, marketplaceId])(DATA);
    const profile = profiles.getIn(['byId', profileId]);
    let template = profile.get('template', EMPTY.MAP).delete('loading');

    if (getSize(template) === 0) return;

    const sections = profile.get('sections');
    const category = channel === EBAY
      ? template.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE])
      : template.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);

    const itemSpecifics = template.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);

    template = template.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS],
      updateItemSpecificsForCategory({ category, channel, itemSpecifics, taxonomyData })
    );

    if (template.hasIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS])) {
      const productDetails = template.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP);

      template = template.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS],
        updateProductDetailsForCategory({ category, channel, productDetails, taxonomyData })
      );
    }

    template = setIsComplete({ sections, template: setProductErrors({ product: template, shopData, taxonomyData }) });

    dispatch(actions.profiles.setData({ path: ['byId', profileId, 'template'], value: template }));

    if (profiles.getIn(['editing', 'id']) === profileId) {
      dispatch(actions.profiles.setData({ path: ['editing', 'template'], value: template }));
    }
  };

  return state;
}

const profiles = {
  [actions.profiles.bootstrap]: bootstrap,
  [actions.profiles.cleanUp]: cleanUp,
  [actions.profiles.connectListings]: connectListings,
  [actions.profiles.connectListings.fulfilled]: connectListingsFulfilled,
  [actions.profiles.connectListings.rejected]: connectListingsRejected,
  [actions.profiles.copyProfile]: copyProfile,
  [actions.profiles.copyProfile.fulfilled]: copyProfileFulfilled,
  [actions.profiles.copyProfile.rejected]: copyProfileRejected,
  [actions.profiles.deleteProfile]: deleteProfile,
  [actions.profiles.deleteProfile.fulfilled]: deleteProfileFulfilled,
  [actions.profiles.deleteProfile.rejected]: deleteProfileRejected,
  [actions.profiles.disconnectListing]: disconnectListing,
  [actions.profiles.disconnectListing.fulfilled]: disconnectListingFulfilled,
  [actions.profiles.disconnectListing.rejected]: disconnectListingRejected,
  [actions.profiles.edit]: edit,
  [actions.profiles.getAiOperations]: getAiOperations,
  [actions.profiles.getAiOperations.fulfilled]: getAiOperationsFulfilled,
  [actions.profiles.getAiOperations.rejected]: getAiOperationsRejected,
  [actions.profiles.getFilters]: getFilters,
  [actions.profiles.getFilters.fulfilled]: getFiltersFulfilled,
  [actions.profiles.getFilters.rejected]: getFiltersRejected,
  [actions.profiles.getListings]: getListings,
  [actions.profiles.getListings.fulfilled]: getListingsFulfilled,
  [actions.profiles.getListings.rejected]: getListingsRejected,
  [actions.profiles.getProfiles]: getProfiles,
  [actions.profiles.getProfiles.fulfilled]: getProfilesFulfilled,
  [actions.profiles.getProfiles.rejected]: getProfilesRejected,
  [actions.profiles.getTemplates]: getTemplates,
  [actions.profiles.getTemplates.fulfilled]: getTemplatesFulfilled,
  [actions.profiles.getTemplates.rejected]: getTemplatesRejected,
  [actions.profiles.saveProfile]: saveProfile,
  [actions.profiles.saveProfile.fulfilled]: saveProfileFulfilled,
  [actions.profiles.saveProfile.rejected]: saveProfileRejected,
  [actions.profiles.search]: search,
  [actions.profiles.setData]: setData,
  [actions.profiles.setModal]: setModal,
  [actions.profiles.setPage]: setPage,
  [actions.profiles.setSection]: setSection,
  [actions.profiles.setType]: setType,
  [actions.profiles.showConnectModal]: showConnectModal,
  [actions.profiles.showConnectModal.fulfilled]: showConnectModalFulfilled,
  [actions.profiles.showConnectModal.rejected]: showConnectModalRejected,
  [actions.profiles.toggleTemplateMode]: toggleTemplateMode,
  [actions.profiles.toggleTemplateMode.fulfilled]: toggleTemplateModeFulfilled,
  [actions.profiles.toggleTemplateMode.rejected]: toggleTemplateModeRejected,
  [actions.profiles.updateTemplateAttributes]: updateTemplateAttributes,
};

export default profiles;
